import { useRouter } from 'next/compat/router'
import { usePathname } from 'next/navigation'
import { useMemo } from 'react'

/**
 * Hook to check if we should show a ChatGPT back link.
 * Returns the decoded redirectUrl if:
 * - We're on a chats page
 * - There's a redirectUrl query parameter
 * - The redirectUrl hostname matches 'chatgpt.com' or 'www.chatgpt.com'
 * Otherwise returns null.
 */
const ALLOWED_CHATGPT_HOSTS = ['chatgpt.com', 'www.chatgpt.com']

export const useChatGPTBackLink = (): string | null => {
  // next/compat/router: the pages NextRouter under pages/, null under app/
  // (next/router's useRouter throws when unmounted, so it can't be used in
  // components shared with the App Router, e.g. via Layout → LayoutHeader).
  const compatRouter = useRouter()
  const appPathname = usePathname()
  const pathname = compatRouter ? compatRouter.asPath : appPathname
  // Under pages/, preserve the isReady gate; app/ has no equivalent (the
  // pathname is authoritative immediately).
  const isReady = compatRouter ? compatRouter.isReady : true

  return useMemo(() => {
    // Early returns: client-side check and route validation
    if (
      typeof window === 'undefined' ||
      !isReady ||
      !pathname?.startsWith('/chats/')
    ) {
      return null
    }

    // Extract and validate redirectUrl parameter
    try {
      const url = new URL(window.location.href)
      const redirectUrl = url.searchParams.get('redirectUrl')

      if (!redirectUrl) {
        return null
      }

      // Decode and validate ChatGPT URL by checking hostname
      try {
        const decodedUrl = decodeURIComponent(redirectUrl)
        const redirectUrlObj = new URL(decodedUrl)
        const hostname = redirectUrlObj.hostname.toLowerCase()

        // Strict allowlist check to prevent subdomain attacks
        // Only allow exact matches, not endsWith() which would allow malicious-chatgpt.com
        if (ALLOWED_CHATGPT_HOSTS.includes(hostname)) {
          return decodedUrl
        }
      } catch (_e) {
        // Invalid URL encoding or malformed URL, ignore
      }
    } catch (_e) {
      // Invalid URL or window.location.href, ignore
    }

    return null
  }, [pathname, isReady])
}
